import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * @type {import('react').Context<{
 *   active: any | null,
 *   open: any[],
 *   live: any | null,
 *   schedule: any | null,
 *   areDonationsOpen: boolean,
 * }>}
 */
const EventContext = createContext({
  active: null,
  open: [],
  live: null,
  schedule: null,
  areDonationsOpen: false,
});
EventContext.displayName = 'EventContext';

export const useEvent = () => useContext(EventContext);

export function EventProvider({
  children,
  initialActive = null,
  initialOpen,
  initialLive = null,
  initialSchedule = null,
}) {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState([]);
  const [live, setLive] = useState(null);
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    setActive(initialActive);
    setOpen(initialOpen);
    setLive(initialLive);
    setSchedule(initialSchedule);
  }, [initialActive, initialOpen, initialLive, initialSchedule]);

  const areDonationsOpen = useMemo(() => {
    if (!active?.acceptDonations) return false;

    const now = new Date();
    const start = active.donationStartAt ? new Date(active.donationStartAt) : null;
    const end = active.donationEndAt ? new Date(active.donationEndAt) : null;

    if (start && now < start) return false;
    if (end && now > end) return false;

    return true;
  }, [active]);

  const value = useMemo(
    () => ({
      active,
      open,
      live,
      schedule,
      areDonationsOpen,
    }),
    [active, open, live, schedule, areDonationsOpen],
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

EventProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialActive: PropTypes.object,
  initialLive: PropTypes.object,
  initialOpen: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialSchedule: PropTypes.object,
};
